// Per-venue token + device-id storage, scoped to the venue key on the venue's own origin.
// Tokens live in sessionStorage (narrow lifetime; no cookies — 3rd-party cookies are unreliable).
import type { AuthResult } from './types';

const PREFIX = '_e365';
const k = (venueKey: string, name: string) => `${PREFIX}:${venueKey}:${name}`;

export class SessionStore {
  constructor(private venueKey: string) {}

  get accessToken(): string | null {
    try { return sessionStorage.getItem(k(this.venueKey, 'at')); } catch { return null; }
  }

  get refreshToken(): string | null {
    try { return sessionStorage.getItem(k(this.venueKey, 'rt')); } catch { return null; }
  }

  setSession(auth: AuthResult): void {
    try {
      sessionStorage.setItem(k(this.venueKey, 'at'), auth.accessToken);
      sessionStorage.setItem(k(this.venueKey, 'rt'), auth.refreshToken);
      if (auth.user?.email) sessionStorage.setItem(k(this.venueKey, 'email'), auth.user.email);
      const name = [auth.user?.firstName, auth.user?.lastName].filter(Boolean).join(' ').trim();
      if (name) sessionStorage.setItem(k(this.venueKey, 'name'), name);
    } catch {
      // sessionStorage unavailable (private mode / blocked) — tokens just won't persist.
    }
  }

  /** The signed-in user's name + email (from the auth result), used as booking contact info. */
  get profile(): { name: string; email: string } | null {
    try {
      const email = sessionStorage.getItem(k(this.venueKey, 'email'));
      if (!email) return null;
      return { name: sessionStorage.getItem(k(this.venueKey, 'name')) || '', email };
    } catch { return null; }
  }

  clear(): void {
    try {
      ['at', 'rt', 'email', 'name'].forEach(n => sessionStorage.removeItem(k(this.venueKey, n)));
    } catch { /* ignore */ }
  }

  // Pending action to resume after a redirect login (e.g. the checkout the user clicked).
  // MUST use localStorage, not sessionStorage: the login flow is cross-origin
  // (venue site → 365sports.online → back), and sessionStorage does NOT reliably survive a cross-origin
  // navigation round-trip (notably on Safari/iOS). localStorage does. Wrapped with a 15-min TTL so a
  // login that's abandoned mid-flow can't trigger a stale resume on a later visit.
  private static readonly PENDING_TTL_MS = 15 * 60 * 1000;
  setPending(json: string): void {
    try {
      const ts = (typeof Date !== 'undefined') ? Date.now() : 0;
      localStorage.setItem(k(this.venueKey, 'pending'), JSON.stringify({ t: ts, d: json }));
    } catch { /* ignore */ }
  }
  getPending(): string | null {
    try {
      const raw = localStorage.getItem(k(this.venueKey, 'pending'));
      if (!raw) return null;
      const o = JSON.parse(raw) as { t: number; d: string };
      if (Date.now() - o.t > SessionStore.PENDING_TTL_MS) { this.clearPending(); return null; }
      return o.d;
    } catch { return null; }
  }
  clearPending(): void {
    try { localStorage.removeItem(k(this.venueKey, 'pending')); } catch { /* ignore */ }
  }

  /** Stable device id for the watch heartbeat / concurrent-view enforcement. */
  deviceId(): string {
    const key = k(this.venueKey, 'did');
    try {
      let id = localStorage.getItem(key);
      if (!id) { id = randomId(); localStorage.setItem(key, id); }
      return id;
    } catch {
      return randomId();
    }
  }
}

function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch { /* ignore */ }
  return 'd-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
